<script>
import axios from '@/utils/axios';
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

export default {
  // Define props that parent can pass
  props: {
    leadId: {
      type: [String, Number],
      required: true
    },
    // Optional: allow parent to pre-populate data
    initialData: {
      type: Object,
      default: () => ({})
    },
    isEditing: {
     type: Boolean,
     default: false
    }
  },

  // Document what events this component emits
  emits: ['reminder-created', 'reminder-error'],

  inject: ['setLoading'],    

  data() {
    return {
      editor: ClassicEditor,
      editorConfig: {},
      errors: {}, 
      id: null,
      title: '',
      description: '',
      reminder_type: '',
      reminder_datetime: '',
      advance_notification: '',
      types: ['FollowUp','Meeting','Call','Email','Other'],
    };
  },

  // Watch for initial data changes
  watch: {
   initialData: {
    handler(newData){
     if(newData && Object.keys(newData).length > 0){
      this.populateForm(newData);
     }
    },
    immediate: true
   }
  },

  methods: {
   populateForm(data) {
    this.id = data.id || null;  
    this.title = data.title || '';
    this.description = data.description || '';
    this.reminder_type = data.reminder_type || '';
    this.reminder_datetime = data.reminder_datetime || '';
    this.advance_notification = data.advance_notification || '';
   },

   async submitReminder() {
    this.errors = {};  
     try{
      const payload = {
        lead_id: this.leadId,
        title: this.title,
        description: this.description,
        reminder_type: this.reminder_type,
        reminder_datetime: this.reminder_datetime,   
        advance_notification: this.advance_notification,
      };
        
      let response = [];
      if(this.isEditing && this.id){
       response = await axios.put(`/reminders/${this.id}/update`, payload); 
      }else{
       response = await axios.post(`/reminders/create`, payload);
      }
        
      // Emit success event to parent
      this.$emit('reminder-created', response.data);
        
      if(!this.isEditing){
       this.resetForm();
      }

      return true;
     }catch(error){
      if(error.type === 'validation'){
       this.errors = error.errors; 
      }else{
       this.$swalToast(error.message || 'Failed to create reminder', 'error');
       // Emit error event to parent
       this.$emit('reminder-error', error);
      }
      return false; // Return failure
     }
    },

    resetForm() {
     this.id = null;
     this.title = '';
     this.description = '';
     this.reminder_type = '';
     this.reminder_datetime = '';
     this.advance_notification = '';
     this.errors = {};
    },

    // Get current form data (useful for parent)
    getFormData() {
     return {
      title: this.title,
      description: this.description,
      reminder_type: this.reminder_type,
      reminder_datetime: this.reminder_datetime,
      advance_notification: this.advance_notification
     };
    }
  }
}
</script>

<template>
  <div class="container">
    <div class="row">
      <!-- Form fields remain the same -->
      <div class="col-12">
        <div class="mb-3">
          <label class="form-label">Title</label>
          <input type="text" class="form-control" v-model="title" />
          <div v-if="errors.title" class="text-danger small">{{ errors.title[0] }}</div>
        </div>
      </div>

      <div class="col-6">
        <div class="mb-3">
          <label class="form-label">Type</label>
          <vue-select :options="types" placeholder="Select" v-model="reminder_type"/>
          <div v-if="errors.reminder_type" class="text-danger small">{{ errors.reminder_type[0] }}</div>
        </div>
      </div>

      <div class="col-6">
        <div class="mb-3">
          <label class="form-label">Date-Time</label>
          <input type="datetime-local" class="form-control" v-model="reminder_datetime" />
          <div v-if="errors.reminder_datetime" class="text-danger small">{{ errors.reminder_datetime[0] }}</div>
        </div>
      </div>

      <div class="col-12">
        <div class="mb-3">
          <label class="form-label">Notify Before</label> <br>
          
          <div class="form-check form-check-inline form-checkbox-success mb-2">
            <input type="radio" class="form-check-input me-2" name="advance_notification" id="notify_15" value="15_minutes" v-model="advance_notification">
            <label class="form-check-label" for="notify_15">15 minutes</label>
          </div>

          <div class="form-check form-check-inline form-checkbox-success mb-2">
            <input type="radio" class="form-check-input me-2" name="advance_notification" id="notify_30" value="30_minutes" v-model="advance_notification">
            <label class="form-check-label" for="notify_30">30 minutes</label>  
          </div>

          <div class="form-check form-check-inline form-checkbox-success mb-2">
            <input type="radio" class="form-check-input me-2" name="advance_notification" id="notify_1hour" value="1_hour" v-model="advance_notification">
            <label class="form-check-label" for="notify_1hour">1 hour</label>  
          </div>

          <div class="form-check form-check-inline form-checkbox-success mb-2">
            <input type="radio" class="form-check-input me-2" name="advance_notification" id="notify_2hour" value="2_hours" v-model="advance_notification">
            <label class="form-check-label" for="notify_2hour">2 hour</label>  
          </div>

          <div class="form-check form-check-inline form-checkbox-success mb-2">
            <input type="radio" class="form-check-input me-2" name="advance_notification" id="notify_1day" value="1_day" v-model="advance_notification">
            <label class="form-check-label" for="notify_1day">1 day</label>  
          </div>

          <div v-if="errors.advance_notification" class="text-danger small mt-1">{{ errors.advance_notification[0] }}</div>
        </div>
      </div>

      <div class="col-lg-12">
        <div class="mb-3">
          <label class="form-label">Descriptions</label>
          <ckeditor :editor="editor" v-model="description" :config="editorConfig"></ckeditor>
          <div v-if="errors.description" class="text-danger small">{{ errors.description[0] }}</div>
        </div>
      </div>
    </div>  
  </div>
</template>
